/**
 * Tracer-parity action icons for an AdTech registration_id:
 * Save to watchlist · Notes · Load PoL · Add to Intel Packet.
 *
 * Self-contained: owns save/notes dialogs and watchlist membership state.
 * Parent only needs to pass onLoadPol (+ optional courseId).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  Tooltip,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import TimelineIcon from '@mui/icons-material/Timeline';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import pathfinderApi, { SavedDevice } from '../../services/pathfinderApi';
import PromoteToPacketDialog, { PromoteTarget } from '@signals/app-shell/components/intel/PromoteToPacketDialog';

export interface AdtechDeviceActionsProps {
  registrationId: string;
  courseId?: string;
  /** Load full pattern-of-life for this device. */
  onLoadPol?: (registrationIds: string[], label?: string) => void;
  polLoading?: boolean;
  /** Hide individual actions when not needed. */
  showSave?: boolean;
  showNotes?: boolean;
  showPol?: boolean;
  showIntel?: boolean;
  iconFontSize?: number;
}

function shortName(rid: string): string {
  const clean = rid.replace(/^GN-/i, '');
  return `Device ${clean.slice(0, 8)}`;
}

function cleanRegId(rid: string): string {
  return rid.replace(/^GN-/i, '').trim();
}

const AdtechDeviceActions: React.FC<AdtechDeviceActionsProps> = ({
  registrationId,
  courseId,
  onLoadPol,
  polLoading = false,
  showSave = true,
  showNotes = true,
  showPol = true,
  showIntel = true,
  iconFontSize = 14,
}) => {
  const rid = cleanRegId(registrationId);
  const [saved, setSaved] = useState<SavedDevice | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState(shortName(rid));
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [toast, setToast] = useState<{ severity: 'success' | 'error' | 'info'; msg: string } | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<PromoteTarget | null>(null);

  const refreshSaved = useCallback(async () => {
    if (!rid) return;
    try {
      const list = await pathfinderApi.getSavedDevices(courseId);
      const hit = list.find(
        (d) => cleanRegId(d.registration_id) === rid || d.registration_id === registrationId,
      ) || null;
      setSaved(hit);
    } catch {
      /* ignore — actions still usable */
    }
  }, [rid, registrationId, courseId]);

  useEffect(() => {
    refreshSaved();
    const handler = () => refreshSaved();
    window.addEventListener('refresh-saved-devices', handler);
    return () => window.removeEventListener('refresh-saved-devices', handler);
  }, [refreshSaved]);

  const ensureSaved = useCallback(async (name?: string): Promise<SavedDevice | null> => {
    if (saved) return saved;
    const created = await pathfinderApi.saveDevice({
      registrationId: rid,
      name: name || shortName(rid),
      deviceKind: 'adtech',
      courseId,
    });
    setSaved(created);
    window.dispatchEvent(new Event('refresh-saved-devices'));
    return created;
  }, [saved, rid, courseId]);

  const handleSaveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (saved) {
      setToast({ severity: 'info', msg: 'Already on Saved Devices watchlist' });
      return;
    }
    setSaveName(shortName(rid));
    setSaveOpen(true);
  };

  const handleConfirmSave = async () => {
    setBusy(true);
    try {
      await ensureSaved(saveName.trim() || shortName(rid));
      setSaveOpen(false);
      setToast({ severity: 'success', msg: 'Saved to watchlist' });
    } catch {
      setToast({ severity: 'error', msg: 'Failed to save device' });
    } finally {
      setBusy(false);
    }
  };

  const handleNotesClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const entry = saved || await ensureSaved();
      setNotesDraft(entry?.notes || '');
      setNotesOpen(true);
    } catch {
      setToast({ severity: 'error', msg: 'Could not open notes (save failed)' });
    } finally {
      setBusy(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!saved) return;
    setBusy(true);
    try {
      const updated = await pathfinderApi.updateSavedDevice(saved.id, { notes: notesDraft });
      setSaved(updated);
      setNotesOpen(false);
      window.dispatchEvent(new Event('refresh-saved-devices'));
      setToast({ severity: 'success', msg: 'Notes saved' });
    } catch {
      setToast({ severity: 'error', msg: 'Failed to save notes' });
    } finally {
      setBusy(false);
    }
  };

  const handleLoadPol = (e: React.MouseEvent) => {
    e.stopPropagation();
    onLoadPol?.([rid], `AdTech PoL ${rid.slice(0, 8)}`);
  };

  const handleIntel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const entry = saved || await ensureSaved().catch(() => null);
    setPromoteTarget({
      member_type: 'adtech_id',
      member_id: rid,
      label: entry?.name || shortName(rid),
      is_primary: true,
    });
  };

  if (!rid) return null;

  return (
    <>
      <Box
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.1 }}
        onClick={(e) => e.stopPropagation()}
      >
        {showSave && (
          <Tooltip title={saved ? 'On Saved Devices watchlist' : 'Save to watchlist'}>
            <span>
              <IconButton size="small" disabled={busy} onClick={handleSaveClick} sx={{ p: 0.25 }}>
                {saved
                  ? <StarIcon sx={{ fontSize: iconFontSize, color: '#FFD700' }} />
                  : <StarBorderIcon sx={{ fontSize: iconFontSize }} />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {showNotes && (
          <Tooltip title="Notes">
            <span>
              <IconButton size="small" disabled={busy} onClick={handleNotesClick} sx={{ p: 0.25 }}>
                <StickyNote2OutlinedIcon
                  sx={{ fontSize: iconFontSize, color: saved?.notes ? 'warning.main' : 'inherit' }}
                />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {showPol && onLoadPol && (
          <Tooltip title="Load pattern of life">
            <span>
              <IconButton size="small" disabled={busy || polLoading} onClick={handleLoadPol} sx={{ p: 0.25 }}>
                {polLoading
                  ? <CircularProgress size={iconFontSize} />
                  : <TimelineIcon sx={{ fontSize: iconFontSize, color: '#7b1fa2' }} />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {showIntel && (
          <Tooltip title="Promote to Intel Packet">
            <span>
              <IconButton size="small" disabled={busy} onClick={handleIntel} sx={{ p: 0.25 }}>
                <TravelExploreIcon sx={{ fontSize: iconFontSize, color: 'info.main' }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <Dialog open={saveOpen} onClose={() => !busy && setSaveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save to watchlist</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Watchlist name"
            fullWidth
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmSave(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmSave} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={notesOpen} onClose={() => !busy && setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Notes — {saved?.name || shortName(rid)}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            minRows={4}
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNotes} disabled={busy || !saved}>
            {busy ? 'Saving…' : 'Save notes'}
          </Button>
        </DialogActions>
      </Dialog>

      <PromoteToPacketDialog
        open={!!promoteTarget}
        onClose={() => setPromoteTarget(null)}
        target={promoteTarget}
        defaultName={promoteTarget?.label ? `AdTech: ${promoteTarget.label}` : undefined}
        onPromoted={() => setToast({ severity: 'success', msg: 'Promoted to Intel Packet' })}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default AdtechDeviceActions;
